import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Search,
  UserX,
  CalendarDays,
  Calculator,
  FileText,
} from 'lucide-react';
import { payrollRunApi } from '../services/api';
import type {
  EmployeePayrollCalculationResultDto,
  PayrollEmployeeAuditDto,
  PayrollExcludedEmployeeAuditDto,
  PayrollRunAuditSummaryDto,
  PayrollRunPreviewDto,
  PayrollTreatmentSummaryDto,
} from '../types';

function formatYmd(iso: string | undefined | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

function money(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}€${Math.abs(n).toFixed(2)}`;
}

function isTimecardField(label: string): boolean {
  const l = label.toLowerCase();
  return l.includes('timecard');
}

function isLeaveField(label: string): boolean {
  const l = label.toLowerCase();
  return l.startsWith('leave ') || l.includes('leave day');
}

const FieldRows: React.FC<{
  fields: { label: string; value: string; source?: string | null }[];
}> = ({ fields }) => (
  <dl className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
    {fields.map((f, i) => (
      <div key={`${f.label}-${i}`} className="grid grid-cols-1 sm:grid-cols-[minmax(10rem,14rem)_1fr] gap-1 sm:gap-4 px-3 py-2.5">
        <dt className="text-xs font-medium text-neutral-500 sm:pt-0.5">{f.label}</dt>
        <dd>
          <div className="text-sm text-neutral-900 break-words">{f.value}</div>
          {f.source ? <div className="text-[11px] text-neutral-400 mt-0.5">{f.source}</div> : null}
        </dd>
      </div>
    ))}
  </dl>
);

const TreatmentSummaryBlock: React.FC<{ summary: PayrollTreatmentSummaryDto }> = ({ summary }) => {
  const rows: { label: string; value: string }[] = [
    { label: 'Position', value: summary.position },
    { label: 'Pay rate', value: summary.payRate },
    { label: 'Leave', value: summary.leave },
    { label: 'BH not worked', value: summary.bhNotWorked },
    { label: 'BH worked', value: summary.bhWorked },
  ];

  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-sky-900/70 mb-2">
        Payroll treatment
      </div>
      <dl className="grid grid-cols-1 gap-2 sm:gap-1.5">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-1 sm:grid-cols-[7.5rem_1fr] gap-0.5 sm:gap-3">
            <dt className="text-xs font-medium text-sky-900/60 sm:pt-0.5">{r.label}</dt>
            <dd className="text-sm text-neutral-900 leading-snug">{r.value || '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const EmployeeDetailPanel: React.FC<{
  emp: PayrollEmployeeAuditDto;
  loading?: boolean;
}> = ({ emp, loading }) => {
  const cashLines = useMemo(
    () => emp.lines.filter((l) => l.amount !== 0 || (l.category || '').toLowerCase().includes('accrual')),
    [emp.lines]
  );
  const payingLines = useMemo(() => emp.lines.filter((l) => l.amount !== 0), [emp.lines]);
  const timecardFields = useMemo(() => emp.inputs.filter((f) => isTimecardField(f.label)), [emp.inputs]);
  const leaveFields = useMemo(() => emp.inputs.filter((f) => isLeaveField(f.label)), [emp.inputs]);
  const otherFields = useMemo(
    () => emp.inputs.filter((f) => !isTimecardField(f.label) && !isLeaveField(f.label)),
    [emp.inputs]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-neutral-600 py-16">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading calculation…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Result hero */}
      <div className="rounded-xl border border-neutral-200 bg-white px-5 py-5">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-neutral-900 truncate">{emp.employeeName}</h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            Employee #{emp.employeeId}
            {!emp.isEmployeeActive ? ' · Inactive' : ''}
          </p>
        </div>

        {emp.treatmentSummary ? <TreatmentSummaryBlock summary={emp.treatmentSummary} /> : null}

        <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-neutral-100 pt-4">
          <div className="text-xs uppercase tracking-wide text-neutral-500">Calculated gross</div>
          <div className="text-right">
            <div
              className={`text-3xl font-semibold tabular-nums ${
                emp.grossTotal < 0 ? 'text-red-700' : 'text-neutral-900'
              }`}
            >
              {money(emp.grossTotal)}
            </div>
            <div className="mt-1 flex justify-end">
              {emp.success ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-xs font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" /> OK
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 text-xs font-medium">
                  <AlertTriangle className="w-3.5 h-3.5" /> Errors
                </span>
              )}
            </div>
          </div>
        </div>

        {emp.errors?.length > 0 && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 space-y-1">
            {emp.errors.map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </div>
        )}
        {emp.warnings?.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-1">
            {emp.warnings.map((w, i) => (
              <div key={i} className="flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pay lines — simple receipt */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-4 h-4 text-neutral-500" />
          <h3 className="text-sm font-semibold text-neutral-900">Pay breakdown</h3>
        </div>
        {payingLines.length === 0 && cashLines.length === 0 ? (
          <p className="text-sm text-neutral-500 rounded-lg border border-dashed border-neutral-200 bg-white px-4 py-6 text-center">
            No pay lines for this employee in the period.
          </p>
        ) : (
          <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-left text-neutral-500 border-b border-neutral-200">
                  <th className="px-4 py-2.5 font-medium">What</th>
                  <th className="px-3 py-2.5 font-medium whitespace-nowrap">Date</th>
                  <th className="px-3 py-2.5 font-medium text-right">Hours</th>
                  <th className="px-3 py-2.5 font-medium text-right">Rate</th>
                  <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(payingLines.length > 0 ? payingLines : cashLines).map((l, i) => (
                  <tr key={i} className="border-t border-neutral-100 align-top">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-neutral-900">{l.category}</div>
                      {l.detail ? (
                        <div className="text-xs text-neutral-500 mt-0.5 max-w-md leading-relaxed">{l.detail}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-neutral-600">{formatYmd(l.date)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-neutral-800">
                      {l.hours ? l.hours.toFixed(2) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-neutral-800">
                      {l.hourlyRateApplied ? `€${l.hourlyRateApplied.toFixed(2)}` : '—'}
                      {l.multiplier != null && l.multiplier !== 1 ? (
                        <span className="text-neutral-400 text-xs block">×{l.multiplier.toFixed(2)}</span>
                      ) : null}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-mono font-medium ${
                        l.amount < 0 ? 'text-red-700' : 'text-neutral-900'
                      }`}
                    >
                      {money(l.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-neutral-200 bg-neutral-50">
                  <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-neutral-700 text-right">
                    Total gross
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-mono text-base font-semibold ${
                      emp.grossTotal < 0 ? 'text-red-700' : 'text-neutral-900'
                    }`}
                  >
                    {money(emp.grossTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* How calculated */}
      {emp.steps.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-neutral-500" />
            <h3 className="text-sm font-semibold text-neutral-900">How this was calculated</h3>
          </div>
          <ol className="space-y-2">
            {emp.steps.map((s, i) => (
              <li key={i} className="rounded-lg border border-neutral-200 bg-white px-4 py-3">
                <div className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-700">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-neutral-900">{s.title}</div>
                    <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{s.explanation}</p>
                    {s.formula ? (
                      <p className="mt-2 font-mono text-xs text-neutral-800 bg-neutral-50 border border-neutral-200 rounded-md px-2.5 py-1.5">
                        {s.formula}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Reference details below */}
      <section className="space-y-4 pt-2 border-t border-neutral-200">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-neutral-500" />
          <h3 className="text-sm font-semibold text-neutral-900">Reference details</h3>
          <span className="text-xs text-neutral-500">Timecards, leave, and inputs used for this run</span>
        </div>

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Timecards</h4>
          {timecardFields.length > 0 ? (
            <FieldRows fields={timecardFields} />
          ) : (
            <p className="text-sm text-neutral-500">No timecard fields in this audit.</p>
          )}
        </div>

        {leaveFields.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Leave</h4>
            <FieldRows fields={leaveFields} />
          </div>
        )}

        {otherFields.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Other inputs</h4>
            <FieldRows fields={otherFields} />
          </div>
        )}

        <p className="text-xs text-neutral-500">
          Profile:{' '}
          <Link className="text-blue-700 hover:underline" to={`/employees/${emp.employeeId}`}>
            Open employee #{emp.employeeId}
          </Link>
        </p>
      </section>
    </div>
  );
};

const ExcludedDetail: React.FC<{ emp: PayrollExcludedEmployeeAuditDto }> = ({ emp }) => (
  <div className="space-y-4">
    <div className="rounded-xl border border-neutral-200 bg-white px-5 py-5">
      <div className="flex items-start gap-3">
        <UserX className="w-5 h-5 text-neutral-500 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">{emp.employeeName}</h2>
          <p className="text-sm text-neutral-500 mt-0.5">Employee #{emp.employeeId}</p>
          <div className="mt-3">
            <span className="inline-flex rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700">
              {emp.reasonCode}
            </span>
            <p className="text-sm font-medium text-neutral-900 mt-2">{emp.reasonTitle}</p>
            <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{emp.guidance}</p>
          </div>
        </div>
      </div>
    </div>
    <div>
      <h3 className="text-sm font-semibold text-neutral-900 mb-2">Why they were excluded</h3>
      <FieldRows fields={emp.fields} />
    </div>
    <p className="text-xs text-neutral-500">
      Profile:{' '}
      <Link className="text-blue-700 hover:underline" to={`/employees/${emp.employeeId}`}>
        Open employee #{emp.employeeId}
      </Link>
    </p>
  </div>
);

const PayrollRunAuditPage: React.FC = () => {
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const runId = Number(runIdParam);
  const navigate = useNavigate();

  const [summary, setSummary] = useState<PayrollRunAuditSummaryDto | null>(null);
  const [preview, setPreview] = useState<PayrollRunPreviewDto | null>(null);
  const [excluded, setExcluded] = useState<PayrollExcludedEmployeeAuditDto[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<PayrollEmployeeAuditDto | null>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const [excludedLoaded, setExcludedLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'included' | 'excluded'>('included');
  const [showZeroGross, setShowZeroGross] = useState(false);

  useEffect(() => {
    setSummary(null);
    setPreview(null);
    setExcluded([]);
    setExcludedLoaded(false);
    setSelectedId(null);
    setSelectedAudit(null);
    setSearch('');
    setTab('included');
  }, [runId]);

  useEffect(() => {
    if (!Number.isFinite(runId) || runId <= 0) {
      setError('Invalid payroll run id.');
      setLoadingPage(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      setError(null);
      try {
        // Preview only on open. Do NOT kick off excluded ClassifyAsync here — it contended
        // with the auto-selected employee's detail request and made the first click ~20s.
        const prev = await payrollRunApi.preview(runId);
        if (cancelled) return;
        setPreview(prev);
        const gross =
          prev.employees?.reduce((s, e) => s + (e.grossTotal || 0), 0) ?? 0;
        setSummary({
          payrollRunId: prev.payrollRunId ?? runId,
          status: '',
          periodStart: prev.periodStart,
          periodEnd: prev.periodEnd,
          notes: null,
          filters: null,
          includedCount: prev.employees?.length ?? 0,
          excludedCount: 0,
          totalGross: gross,
          hasCalculationErrors: !!prev.hasCalculationErrors,
        });
        if (prev.employees?.length) {
          const firstPaid =
            prev.employees.find((e) => e.grossTotal !== 0) ?? prev.employees[0] ?? null;
          if (firstPaid) setSelectedId(firstPaid.employeeId);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string }; status?: number } }).response?.data
                  ?.message
              : null;
          const status =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { status?: number } }).response?.status
              : undefined;
          if (status === 409) {
            setError(msg || 'Payroll records are not ready yet. Wait until status is Ready for View.');
          } else {
            setError(msg || (e instanceof Error ? e.message : 'Failed to load audit.'));
          }
        }
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const loadExcluded = useCallback(async () => {
    if (excludedLoaded || loadingExcluded) return;
    setLoadingExcluded(true);
    try {
      const ex = await payrollRunApi.audit(runId, { section: 'excluded' });
      setExcluded(ex.excluded ?? []);
      setExcludedLoaded(true);
      if (ex.summary) {
        setSummary((prevSum) => ({
          ...ex.summary!,
          totalGross: prevSum?.totalGross ?? ex.summary!.totalGross,
          includedCount:
            prevSum?.includedCount && prevSum.includedCount > 0
              ? prevSum.includedCount
              : ex.summary!.includedCount,
          hasCalculationErrors:
            !!prevSum?.hasCalculationErrors || ex.summary!.hasCalculationErrors,
        }));
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || (e instanceof Error ? e.message : 'Failed to load excluded employees.'));
    } finally {
      setLoadingExcluded(false);
    }
  }, [runId, excludedLoaded, loadingExcluded]);

  useEffect(() => {
    if (tab === 'excluded') void loadExcluded();
  }, [tab, loadExcluded]);

  const loadEmployeeDetail = useCallback(
    async (employeeId: number) => {
      setLoadingDetail(true);
      setSelectedAudit(null);
      try {
        const res = await payrollRunApi.audit(runId, {
          section: 'included',
          employeeId,
          take: 1,
        });
        // Fast employee detail returns a light summary (excludedCount often 0) — merge, don't replace.
        if (res.summary) {
          setSummary((prevSum) =>
            prevSum
              ? {
                  ...prevSum,
                  status: res.summary!.status || prevSum.status,
                  notes: res.summary!.notes ?? prevSum.notes,
                  filters: res.summary!.filters ?? prevSum.filters,
                  hasCalculationErrors:
                    prevSum.hasCalculationErrors || res.summary!.hasCalculationErrors,
                }
              : res.summary!
          );
        }
        const emp = res.included?.[0] ?? null;
        setSelectedAudit(emp);
        if (res.excluded?.length) {
          setExcluded((prev) => {
            const ids = new Set(prev.map((x) => `${x.employeeId}:${x.reasonCode}`));
            const extra = res.excluded!.filter((x) => !ids.has(`${x.employeeId}:${x.reasonCode}`));
            return extra.length ? [...prev, ...extra] : prev;
          });
        }
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(msg || (e instanceof Error ? e.message : 'Failed to load employee calculation.'));
      } finally {
        setLoadingDetail(false);
      }
    },
    [runId]
  );

  useEffect(() => {
    if (tab !== 'included' || selectedId == null) return;
    void loadEmployeeDetail(selectedId);
  }, [tab, selectedId, loadEmployeeDetail]);

  const roster = useMemo(() => {
    const list: EmployeePayrollCalculationResultDto[] = preview?.employees ?? [];
    const q = search.trim().toLowerCase();
    let filtered = list;
    if (q) {
      filtered = list.filter(
        (e) => e.employeeName.toLowerCase().includes(q) || String(e.employeeId).includes(q)
      );
    }
    if (!showZeroGross) {
      const paid = filtered.filter((e) => e.grossTotal !== 0);
      if (paid.length > 0) filtered = paid;
    }
    return [...filtered].sort((a, b) => {
      if (a.grossTotal !== 0 && b.grossTotal === 0) return -1;
      if (a.grossTotal === 0 && b.grossTotal !== 0) return 1;
      return a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: 'base' });
    });
  }, [preview, search, showZeroGross]);

  const filteredExcluded = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return excluded;
    return excluded.filter(
      (e) =>
        e.employeeName.toLowerCase().includes(q) ||
        String(e.employeeId).includes(q) ||
        e.reasonCode.toLowerCase().includes(q) ||
        e.reasonTitle.toLowerCase().includes(q)
    );
  }, [excluded, search]);

  const selectedExcluded = useMemo(
    () => filteredExcluded.find((e) => e.employeeId === selectedId) ?? null,
    [filteredExcluded, selectedId]
  );

  const filterChips = useMemo(() => {
    const f = summary?.filters;
    if (!f) return [] as string[];
    const chips: string[] = [];
    if (f.paymentFrequencyName) chips.push(`Frequency: ${f.paymentFrequencyName}`);
    else if (f.paymentFrequencyId) chips.push(`Frequency #${f.paymentFrequencyId}`);
    if (f.payCategoryFilter && f.payCategoryFilter !== 'both') chips.push(`Category: ${f.payCategoryFilter}`);
    if (f.payrollDepartmentName) chips.push(`Dept: ${f.payrollDepartmentName}`);
    if (f.mainLocationName) chips.push(`Location: ${f.mainLocationName}`);
    if (f.scheduleName) chips.push(`Schedule: ${f.scheduleName}`);
    if (f.positionName) chips.push(`Position: ${f.positionName}`);
    if (f.periodSelectionLabel) chips.push(f.periodSelectionLabel);
    return chips;
  }, [summary]);

  const includedCount = preview?.employees?.length ?? summary?.includedCount ?? 0;
  const totalGross =
    preview?.employees?.reduce((s, e) => s + (e.grossTotal || 0), 0) ?? summary?.totalGross ?? 0;

  if (loadingPage) {
    return (
      <div className="flex-1 flex items-center justify-center gap-2 text-neutral-600 py-16">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading payroll audit…
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-neutral-50 min-h-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div>
          <button
            type="button"
            onClick={() => navigate('/reports/payroll-builder')}
            className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Payroll Builder
          </button>
          <h1 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Audit calculations — run #{summary?.payrollRunId ?? runId}
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            {formatYmd(summary?.periodStart)} → {formatYmd(summary?.periodEnd)} · {summary?.status || '—'}
            {summary?.notes ? ` · ${summary.notes}` : ''}
          </p>
        </div>

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-500">Included</div>
              <div className="text-lg font-semibold">{includedCount}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-500">Excluded</div>
              <div className="text-lg font-semibold">
                {excludedLoaded
                  ? excluded.length || summary.excludedCount
                  : loadingExcluded
                    ? '…'
                    : '—'}
              </div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-500">Total gross</div>
              <div className="text-lg font-semibold">{money(totalGross)}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-500">Calc errors</div>
              <div className="text-lg font-semibold flex items-center gap-1">
                {summary.hasCalculationErrors || preview?.hasCalculationErrors ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-amber-600" /> Yes
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" /> No
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {filterChips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {filterChips.map((c) => (
              <span
                key={c}
                className="inline-flex rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-700"
              >
                {c}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5">
            <button
              type="button"
              onClick={() => {
                setTab('included');
                if (roster[0] && (selectedId == null || !roster.some((r) => r.employeeId === selectedId))) {
                  setSelectedId(roster[0].employeeId);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded-md ${
                tab === 'included' ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              Included ({includedCount})
            </button>
            <button
              type="button"
              onClick={() => {
                setTab('excluded');
                setSelectedAudit(null);
                if (filteredExcluded[0]) setSelectedId(filteredExcluded[0].employeeId);
                else setSelectedId(null);
              }}
              className={`px-3 py-1.5 text-sm rounded-md ${
                tab === 'excluded' ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              Excluded (
              {excludedLoaded
                ? excluded.length || summary?.excludedCount || 0
                : loadingExcluded
                  ? '…'
                  : '—'}
              )
            </button>
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or id…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
            />
          </div>
          {tab === 'included' && (
            <label className="inline-flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showZeroGross}
                onChange={(e) => setShowZeroGross(e.target.checked)}
              />
              Show €0 rows
            </label>
          )}
        </div>

        {/* Master–detail */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(16rem,20rem)_1fr] gap-4 items-start">
          <aside className="bg-white border border-neutral-200 rounded-xl overflow-hidden lg:sticky lg:top-4 max-h-[70vh] flex flex-col">
            <div className="px-3 py-2 border-b border-neutral-200 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {tab === 'included' ? 'Employees' : 'Excluded'}
            </div>
            <div className="overflow-y-auto flex-1">
              {tab === 'included' &&
                roster.map((e) => {
                  const active = selectedId === e.employeeId;
                  return (
                    <button
                      key={e.employeeId}
                      type="button"
                      onClick={() => setSelectedId(e.employeeId)}
                      className={`w-full text-left px-3 py-2.5 border-b border-neutral-100 hover:bg-neutral-50 ${
                        active ? 'bg-sky-50 border-l-2 border-l-sky-600' : 'border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-neutral-900 truncate">{e.employeeName}</div>
                          <div className="text-xs text-neutral-500 font-mono">#{e.employeeId}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div
                            className={`text-sm font-semibold tabular-nums ${
                              e.grossTotal < 0 ? 'text-red-700' : 'text-neutral-900'
                            }`}
                          >
                            {money(e.grossTotal)}
                          </div>
                          {!e.success ? (
                            <span className="text-[10px] text-red-600 font-medium">Error</span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              {tab === 'included' && roster.length === 0 && (
                <p className="text-sm text-neutral-500 px-3 py-8 text-center">No employees match.</p>
              )}
              {tab === 'excluded' &&
                filteredExcluded.map((e) => {
                  const active = selectedId === e.employeeId;
                  return (
                    <button
                      key={`${e.employeeId}-${e.reasonCode}`}
                      type="button"
                      onClick={() => setSelectedId(e.employeeId)}
                      className={`w-full text-left px-3 py-2.5 border-b border-neutral-100 hover:bg-neutral-50 ${
                        active ? 'bg-sky-50 border-l-2 border-l-sky-600' : 'border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="text-sm font-medium text-neutral-900 truncate">{e.employeeName}</div>
                      <div className="text-xs text-neutral-500 mt-0.5 truncate">{e.reasonTitle}</div>
                    </button>
                  );
                })}
              {tab === 'excluded' && filteredExcluded.length === 0 && (
                <p className="text-sm text-neutral-500 px-3 py-8 text-center">No excluded employees.</p>
              )}
            </div>
          </aside>

          <main className="min-w-0 rounded-xl border border-neutral-200 bg-neutral-50/50 p-4 sm:p-5 min-h-[24rem]">
            {tab === 'included' && selectedId != null && (
              <>
                {loadingDetail && !selectedAudit ? (
                  <div className="flex items-center justify-center gap-2 text-neutral-600 py-16">
                    <Loader2 className="w-5 h-5 animate-spin" /> Loading calculation…
                  </div>
                ) : selectedAudit ? (
                  <EmployeeDetailPanel emp={selectedAudit} loading={loadingDetail} />
                ) : (
                  <p className="text-sm text-neutral-500 text-center py-16">
                    Could not load calculation for this employee.
                  </p>
                )}
              </>
            )}
            {tab === 'included' && selectedId == null && (
              <p className="text-sm text-neutral-500 text-center py-16">
                Select an employee to see how their pay was calculated.
              </p>
            )}
            {tab === 'excluded' && selectedExcluded && <ExcludedDetail emp={selectedExcluded} />}
            {tab === 'excluded' && !selectedExcluded && (
              <p className="text-sm text-neutral-500 text-center py-16">
                Select an excluded employee to see why they were left out.
              </p>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PayrollRunAuditPage;
